/**
 * TenantAdminDashboard
 *
 * Dashboard for tenant administrators showing tenant health,
 * resource usage, feature status, and configuration completeness.
 *
 * WQ-086: Tenant Dashboard Architecture (Tier 2)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAdminContext, type TenantContext } from '../../../contexts/AdminContextProvider';
import { resolveIcon } from '../../../components/common/IconResolver';
import TenantKillSwitchCard from './TenantKillSwitchCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HealthSummary {
  tenant_id: string;
  tenant_name: string;
  status: string;
  user_count: number;
  user_limit: number;
  storage_used_mb: number;
  storage_limit_mb: number;
  api_calls_today: number;
  api_call_limit: number;
  features_enabled: number;
  features_available: number;
  ai_enabled: boolean;
  ai_tokens_today: number;
  ai_token_limit: number;
  sso_configured: boolean;
  branding_configured: boolean;
  template_assigned: boolean;
  template_name: string | null;
  isolation_level: string;
  last_activity: string | null;
}

interface TenantAdminDashboardProps {
  tenant?: TenantContext;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, { bg: string; text: string }> = {
    active: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e' },
    suspended: { bg: 'rgba(234, 179, 8, 0.15)', text: '#eab308' },
    pending: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6' },
    archived: { bg: 'rgba(107, 114, 128, 0.15)', text: '#6b7280' },
  };
  const c = colors[status] || colors.pending;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'capitalize',
      background: c.bg,
      color: c.text,
    }}>
      {status}
    </span>
  );
};

const ProgressBar: React.FC<{ value: number; max: number; label: string; unit?: string }> = ({
  value, max, label, unit = '',
}) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const color = pct >= 90 ? '#ef4444' : pct >= 70 ? '#eab308' : '#22c55e';
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{label}</span>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {value.toLocaleString()}{unit} / {max.toLocaleString()}{unit}
        </span>
      </div>
      <div style={{
        height: '6px',
        background: 'var(--bg-secondary, rgba(255,255,255,0.05))',
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: '3px',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
};

const ConfigCheck: React.FC<{ label: string; configured: boolean }> = ({ label, configured }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    borderBottom: '1px solid var(--border-primary, rgba(255,255,255,0.06))',
  }}>
    <div style={{
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: configured ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
      color: configured ? '#22c55e' : '#ef4444',
      fontSize: '12px',
      flexShrink: 0,
    }}>
      {configured ? '✓' : '✗'}
    </div>
    <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{label}</span>
    <span style={{
      marginLeft: 'auto',
      fontSize: '12px',
      color: configured ? '#22c55e' : 'var(--text-muted)',
    }}>
      {configured ? 'Configured' : 'Not set'}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

const DashboardSkeleton: React.FC = () => (
  <div className="page-container">
    <div className="grid grid-cols-4" style={{ marginBottom: 'var(--space-6)' }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="card stat-card card-elevated">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div className="skeleton" style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-lg)' }} />
            <div>
              <div className="skeleton" style={{ width: '80px', height: '28px', marginBottom: '6px', borderRadius: 'var(--radius-sm)' }} />
              <div className="skeleton" style={{ width: '100px', height: '14px', borderRadius: 'var(--radius-sm)' }} />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
      <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
        <div className="skeleton" style={{ width: '150px', height: '20px', marginBottom: '16px', borderRadius: 'var(--radius-sm)' }} />
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton" style={{ width: '100%', height: '30px', marginBottom: '12px', borderRadius: 'var(--radius-sm)' }} />
        ))}
      </div>
      <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
        <div className="skeleton" style={{ width: '150px', height: '20px', marginBottom: '16px', borderRadius: 'var(--radius-sm)' }} />
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton" style={{ width: '100%', height: '24px', marginBottom: '12px', borderRadius: 'var(--radius-sm)' }} />
        ))}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const TenantAdminDashboard: React.FC<TenantAdminDashboardProps> = ({ tenant: tenantProp }) => {
  const { authFetch } = useAuth();
  const { activeTenant } = useAdminContext();
  const tenant = tenantProp || activeTenant;

  const [data, setData] = useState<HealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/tenant-portal/health-summary');
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) return <DashboardSkeleton />;

  if (error && !data) {
    return (
      <div className="page-container">
        <div className="card card-elevated" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
          <h3 style={{ marginBottom: 'var(--space-2)' }}>Unable to load tenant dashboard</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>{error}</p>
          <button className="btn btn-primary" onClick={fetchData}>Try Again</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {resolveIcon('layout-dashboard', 24)}
            {tenant?.name || data.tenant_name || 'Tenant'} Dashboard
          </h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
            <StatusBadge status={data.status} />
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              {data.isolation_level} isolation
            </span>
            {data.template_name && (
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                · {data.template_name}
              </span>
            )}
          </div>
        </div>
        <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
          {resolveIcon('activity', 14)}
          <span style={{ marginLeft: '6px' }}>{loading ? 'Refreshing...' : 'Refresh'}</span>
        </button>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-4" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="card stat-card card-elevated">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
            <div style={{
              width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--accent-primary-muted)', borderRadius: 'var(--radius-lg)',
              color: 'var(--accent-primary)', flexShrink: 0,
            }}>
              {resolveIcon('users', 24)}
            </div>
            <div>
              <div className="stat-value">{data.user_count}</div>
              <div className="stat-label">Users</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                of {data.user_limit} max
              </div>
            </div>
          </div>
        </div>

        <div className="card stat-card card-elevated">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
            <div style={{
              width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--accent-primary-muted)', borderRadius: 'var(--radius-lg)',
              color: 'var(--accent-primary)', flexShrink: 0,
            }}>
              {resolveIcon('zap', 24)}
            </div>
            <div>
              <div className="stat-value">{data.features_enabled}</div>
              <div className="stat-label">Features Enabled</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                of {data.features_available} available
              </div>
            </div>
          </div>
        </div>

        <div className="card stat-card card-elevated">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
            <div style={{
              width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: data.ai_enabled ? 'var(--accent-primary-muted)' : 'rgba(107, 114, 128, 0.1)',
              borderRadius: 'var(--radius-lg)',
              color: data.ai_enabled ? 'var(--accent-primary)' : 'var(--text-muted)', flexShrink: 0,
            }}>
              {resolveIcon('cpu', 24)}
            </div>
            <div>
              <div className="stat-value">{data.ai_enabled ? 'Active' : 'Disabled'}</div>
              <div className="stat-label">AI Services</div>
              {data.ai_enabled && data.ai_token_limit > 0 && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {data.ai_tokens_today.toLocaleString()} / {data.ai_token_limit.toLocaleString()} tokens
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card stat-card card-elevated">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
            <div style={{
              width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--accent-primary-muted)', borderRadius: 'var(--radius-lg)',
              color: 'var(--accent-primary)', flexShrink: 0,
            }}>
              {resolveIcon('activity', 24)}
            </div>
            <div>
              <div className="stat-value">{data.api_calls_today.toLocaleString()}</div>
              <div className="stat-label">API Calls Today</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                of {data.api_call_limit.toLocaleString()} limit
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Per-Tenant AI Kill Switch Card (WQ-177/178 — extracted component) */}
      {tenant?.id && (
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <TenantKillSwitchCard tenantId={tenant.id} tenantName={tenant.name} />
        </div>
      )}

      {/* Two-column layout: Resource Usage + Configuration Health */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-6)',
        marginBottom: 'var(--space-6)',
      }}>
        {/* Resource Usage */}
        <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
          <h3 style={{
            margin: '0 0 var(--space-4)',
            fontSize: 'var(--text-lg, 18px)',
            color: 'var(--text-primary)',
          }}>
            Resource Usage
          </h3>
          <ProgressBar label="Users" value={data.user_count} max={data.user_limit} />
          <ProgressBar label="API Calls (today)" value={data.api_calls_today} max={data.api_call_limit} />
          {data.storage_limit_mb > 0 && (
            <ProgressBar
              label="Storage"
              value={Math.round(data.storage_used_mb)}
              max={Math.round(data.storage_limit_mb)}
              unit=" MB"
            />
          )}
          {data.ai_enabled && data.ai_token_limit > 0 && (
            <ProgressBar label="AI Tokens (today)" value={data.ai_tokens_today} max={data.ai_token_limit} />
          )}
        </div>

        {/* Configuration Health */}
        <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
          <h3 style={{
            margin: '0 0 var(--space-4)',
            fontSize: 'var(--text-lg, 18px)',
            color: 'var(--text-primary)',
          }}>
            Configuration Health
          </h3>
          <ConfigCheck label="SSO Authentication" configured={data.sso_configured} />
          <ConfigCheck label="Custom Branding" configured={data.branding_configured} />
          <ConfigCheck label="Template Assigned" configured={data.template_assigned} />
          <ConfigCheck label="AI Services" configured={data.ai_enabled} />
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
            fontSize: '13px',
            color: 'var(--text-secondary)',
          }}>
            {[data.sso_configured, data.branding_configured, data.template_assigned, data.ai_enabled]
              .filter(Boolean).length} of 4 configured
          </div>
        </div>
      </div>

      {/* Feature Overview */}
      <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
        <h3 style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--text-lg, 18px)',
          color: 'var(--text-primary)',
        }}>
          Feature Overview
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {data.features_enabled > 0 ? (
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', width: '100%' }}>
              {data.features_enabled} features enabled out of {data.features_available} available
            </span>
          ) : (
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              No features enabled. Contact your platform administrator to enable features.
            </span>
          )}
        </div>
        {data.last_activity && (
          <div style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid var(--border-primary, rgba(255,255,255,0.06))',
            fontSize: '13px',
            color: 'var(--text-muted)',
          }}>
            Last activity: {new Date(data.last_activity).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantAdminDashboard;
